import React, { useState, useMemo } from 'react';
import { learnerProgressService } from '../../services/learnerProgressService';
import { executeOpenClozeVerticalSlice } from '../../services/slices/OpenClozeVerticalSlice';
import { executeGenericVerticalSlice, ChoiceQuestionAdapter } from '../../services/slices/GenericVerticalSliceOrchestrator';
import ChoiceGrid from '../../components/common/ChoiceGrid';
import { useUserStore } from '../../stores/useUserStore';
import HoverWord, { renderParsedText } from '../../components/common/HoverWord';
import { speakText } from '../../utils/AudioHelper';
import { BookOpen, Volume2, Sparkles, CheckCircle2, PlayCircle, GraduationCap, ArrowRight, Layers, FileText, RefreshCw, HelpCircle, XCircle, MessageSquare, Type, Trophy, ShoppingBag } from 'lucide-react';
import WordBankMatchingGrid from '../../components/cambridge/WordBankMatchingGrid';
import DialogueAHCompleter from '../../components/cambridge/DialogueAHCompleter';
import InlineTextClozeDropdown from '../../components/cambridge/InlineTextClozeDropdown';
import TextExtractionCompleter from '../../components/cambridge/TextExtractionCompleter';
import GlobalModeToggle from '../../components/cambridge/GlobalModeToggle';
import OpenClozeCompleter from '../../components/cambridge/OpenClozeCompleter';
import NovaMascotStore from '../../components/mascot/NovaMascotStore';
import CLILExplorer from '../../components/cambridge/CLILExplorer';
import { Globe } from 'lucide-react';


import VoiceService from '../../services/voiceService';

export default function WorldDiscoveryHub({ data, weekNumber }) {
  const currentUser = useUserStore((state) => state.currentUser);
  const learnerId = currentUser?.id || currentUser?.username || 'guest_01';
  const userXP = useUserStore((state) => state.userXP || 0);

  const [activeTab, setActiveTab] = useState('webtoon'); // 'webtoon' | 'check'
  const [learnSubTab, setLearnSubTab] = useState('clil_explorer'); // 'clil_explorer' | 'webtoon' | 'rw_part1' | 'rw_part2' | 'rw_part4' | 'rw_part5'
  const [activeFrameIndex, setActiveFrameIndex] = useState(0);
  const [selectedHotspot, setSelectedHotspot] = useState(null);
  const [showMascotStore, setShowMascotStore] = useState(false);
  const [highlightMode, setHighlightMode] = useState('vocab'); // 'clean' | 'vocab' | 'grammar'

  // Cambridge Flyers Reading Part 3 state
  const [r3Answers, setR3Answers] = useState({});
  const [r3Submitted, setR3Submitted] = useState({});

  // Interactive Gap-Fill Story state (Cambridge Reading Part 4 Standard)
  const [storyAnswers, setStoryAnswers] = useState({});
  const [selectedGapId, setSelectedGapId] = useState(1);
  const [storySubmitted, setStorySubmitted] = useState(false);
  const [storyScore, setStoryScore] = useState(null);

  // Check Mode state (10 Cambridge Reading Drills)
  const [checkAnswers, setCheckAnswers] = useState({});
  const [checkScore, setCheckScore] = useState(null);
  const [checkSubmitted, setCheckSubmitted] = useState(false);

  // Cambridge Reading Part 3 Story Title State
  const [selectedStoryTitle, setSelectedStoryTitle] = useState(null);
  const [storyTitleSubmitted, setStoryTitleSubmitted] = useState(false);

  // Audio Cleanup on Tab Switch or Unmount to prevent overlapping TTS audio
  React.useEffect(() => {
    return () => {
      if (typeof VoiceService.stopAudio === 'function') {
        VoiceService.stopAudio();
      } else if (typeof VoiceService.stop === 'function') {
        VoiceService.stop();
      }
    };
  }, [activeTab, learnSubTab]);


    const storyScenes = data?.story_scenes || [];

    const interactiveStory = data?.interactive_story || null;

  const checkQuestions = data?.check_mode_drills || [
    {
      id: 'q1',
      question: 'What did Tom break in the morning?',
      options: ['A) His backpack', 'B) His alarm clock', 'C) His bicycle', 'D) His water bottle'],
      answerIndex: 1
    },
    {
      id: 'q2',
      question: 'How did Tom feel when he woke up late?',
      options: ['A) Energetic', 'B) Clumsy', 'C) Angry', 'D) Excited'],
      answerIndex: 1
    },
    {
      id: 'q3',
      question: 'What did Tom slip on in the kitchen?',
      options: ['A) A rug', 'B) A wet puddle', 'C) A banana peel', 'D) A toy car'],
      answerIndex: 1
    },
    {
      id: 'q4',
      question: 'What did Tom spill over his desk?',
      options: ['A) Milk', 'B) Orange juice', 'C) Water', 'D) Soup'],
      answerIndex: 1
    },
    {
      id: 'q5',
      question: 'What did Tom lose on the bus?',
      options: ['A) His jacket', 'B) His hat', 'C) His backpack', 'D) His homework'],
      answerIndex: 2
    },
    {
      id: 'q6',
      question: 'Who found Tom\'s lost backpack?',
      options: ['A) His teacher', 'B) The bus driver', 'C) Mia', 'D) His mother'],
      answerIndex: 2
    },
    {
      id: 'q7',
      question: 'What did Tom do after spilling the liquid?',
      options: ['A) He ran away', 'B) He apologized to mom', 'C) He went to sleep', 'D) He laughed'],
      answerIndex: 1
    },
    {
      id: 'q8',
      question: 'What is the past simple tense of "break"?',
      options: ['A) Breakings', 'B) Broke', 'C) Breaked', 'D) Broken'],
      answerIndex: 1
    },
    {
      id: 'q9',
      question: 'What is the past simple tense of "lose"?',
      options: ['A) Lost', 'B) Losed', 'C) Losing', 'D) Loses'],
      answerIndex: 0
    },
    {
      id: 'q10',
      question: 'What did Tom promise to be in the future?',
      options: ['A) More careless', 'B) More cautious and careful', 'C) More clumsy', 'D) Faster'],
      answerIndex: 1
    }
  ];

  const readingPart3Data = data?.reading_part3_story || {
    title: "Story Discovery",
    story_text: "",
    questions: []
  };

  const currentFrame = storyScenes[activeFrameIndex] || storyScenes[0];
  const frameHotspots = currentFrame.lexical_chunks || [];

  // Grammar X-Ray target patterns from data (or W33 defaults) — Strict While + Past Continuous seeding
  const targetGrammarRegex = data?.target_grammar_regex || data?.clil_article?.target_grammar_regex || [
    { pattern: '\\bwhile\\s+[^,.]*?\\b(was|were)\\s+\\w+ing\\b', label: 'While + Past Continuous Clause', color: '#fef9c3' },
    { pattern: '\\b(was|were)\\s+\\w+ing\\b', label: 'Past Continuous Verb', color: '#fef08a' }
  ];

  // System Global Text Parser using HoverWord component (Disables hover popups in Check Mode)
  const handleRenderParsedText = (text, themeColor = 'indigo') => {
    const isCheckMode = activeTab === 'check';
    return renderParsedText(
      text, themeColor,
      (w) => speakText(w, null, 1.0, null, 'reading', weekNumber, 'advanced'),
      isCheckMode,
      isCheckMode ? 'clean' : highlightMode,
      targetGrammarRegex
    );
  };

  // Play audio chunk via Primary Google Cloud TTS Direct with Browser fallback
  const handleHotspotClick = async (hs) => {
    setSelectedHotspot(hs);
    try {
      await VoiceService.speak(hs.chunk || hs.word, 'read');
    } catch (err) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(hs.chunk || hs.word);
        utterance.lang = 'en-US';
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  // Submit Cambridge Check Mode Exam (10 Questions)
  const handleCheckSubmit = async () => {
    let correctCount = 0;
    checkQuestions.forEach((q) => {
      if (checkAnswers[q.id] === q.answerIndex) {
        correctCount += 1;
      }
    });

    const scorePct = Math.round((correctCount / checkQuestions.length) * 100);
    setCheckScore(scorePct);
    setCheckSubmitted(true);

    await learnerProgressService.logAttempt({
      learnerId: 'learner_default_01',
      contentId: `w${weekNumber}_read_check`,
      mode: 'check',
      result: scorePct >= 70 ? 'correct' : 'incorrect',
      score: scorePct,
      timeSpentSeconds: 60
    });
  };

  // Fill in Gap-fill word pill
  const handleSelectWord = (word) => {
    if (!selectedGapId) return;
    setStoryAnswers((prev) => ({
      ...prev,
      [selectedGapId]: word
    }));
    const nextGap = interactiveStory.gaps.find((g) => g.id > selectedGapId && !storyAnswers[g.id]);
    if (nextGap) {
      setSelectedGapId(nextGap.id);
    }
  };

  // Submit Interactive Story Gap-Fill / Open Cloze via Golden Vertical Slice Pipeline
  const handleStorySubmit = async () => {
    const sliceResult = await executeOpenClozeVerticalSlice({
      weekData: { weekId: weekNumber, readingHub: data, interactive_story: interactiveStory },
      userAnswers: storyAnswers,
      learnerId,
      timeSpentSeconds: 45
    });

    setStoryScore(sliceResult.stage3_scoreResult.scorePct);
    setStorySubmitted(true);
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-4 sm:p-6 bg-white text-slate-800 rounded-3xl border border-slate-200 shadow-xl font-sans">
      {/* Top Controls: Mode Toggle, Mascot Store */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <GlobalModeToggle
          activeMode={activeTab === 'webtoon' ? 'learn' : 'check'}
          onModeChange={(mode) => setActiveTab(mode === 'learn' ? 'webtoon' : 'check')}
        />

        <button
          onClick={() => setShowMascotStore(true)}
          className="px-3.5 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-xs font-black transition flex items-center gap-1.5 shadow-md"
        >
          <ShoppingBag size={14} className="text-amber-300" /> Nova Store ({userXP} XP)
        </button>
      </div>

      {showMascotStore && (
        <NovaMascotStore isOpen={showMascotStore} onClose={() => setShowMascotStore(false)} />
      )}

      {activeTab === 'webtoon' ? (
        <div className="space-y-6">
          <div className="w-full p-2 bg-slate-100/90 rounded-2xl border border-slate-200 shadow-inner">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 w-full">
              <button
                type="button"
                onClick={() => setLearnSubTab('clil_explorer')}
                className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
                  learnSubTab === 'clil_explorer' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300' : 'bg-white text-blue-900 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                <Globe size={14} className={learnSubTab === 'clil_explorer' ? 'text-amber-300' : 'text-blue-600'} /> Explorer
              </button>
              <button
                type="button"
                onClick={() => setLearnSubTab('webtoon')}
                className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
                  learnSubTab === 'webtoon' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300' : 'bg-white text-blue-900 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                📖 Story Time
              </button>
              <button
                type="button"
                onClick={() => setLearnSubTab('rw_part1')}
                className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
                  learnSubTab === 'rw_part1' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300' : 'bg-white text-blue-900 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                🧩 Word Match
              </button>
              <button
                type="button"
                onClick={() => setLearnSubTab('rw_part2')}
                className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
                  learnSubTab === 'rw_part2' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300' : 'bg-white text-blue-900 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                💬 Chat Box
              </button>
              <button
                type="button"
                onClick={() => setLearnSubTab('rw_part4')}
                className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
                  learnSubTab === 'rw_part4' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300' : 'bg-white text-blue-900 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                📝 Fill Blanks
              </button>
              <button
                type="button"
                onClick={() => setLearnSubTab('rw_part5')}
                className={`w-full py-2.5 px-3 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 text-center truncate ${
                  learnSubTab === 'rw_part5' ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-300' : 'bg-white text-blue-900 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                🕵️ Detective
              </button>
            </div>
          </div>

          {learnSubTab === 'clil_explorer' && (
            <CLILExplorer
              clilData={data?.clil_article}
              weekNumber={weekNumber}
              highlightMode={highlightMode}
              setHighlightMode={setHighlightMode}
              targetGrammarRegex={targetGrammarRegex}
            />
          )}

          {learnSubTab === 'webtoon' && (
            /* SUB-TAB 1: 3D WEBTOON SCENES & HOTSPOTS */
            <div className="space-y-6">
              {/* Main Pixar 3D Scene Viewport */}
              <div className="relative bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-800 group">
                <div className="w-full h-80 sm:h-96 relative flex items-center justify-center bg-slate-950">
                  <img
                    src={currentFrame.image_url}
                    alt={currentFrame.title_en}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/20" />

                  {/* Hotspot Audio Pins */}
                  {frameHotspots.map((hs, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleHotspotClick(hs)}
                      style={{ left: `${hs.x}%`, top: `${hs.y}%` }}
                      className="absolute transform -translate-x-1/2 -translate-y-1/2 px-3 py-1.5 bg-indigo-600/90 hover:bg-indigo-500 text-white rounded-full text-xs font-black shadow-xl backdrop-blur-md flex items-center gap-1.5 border border-indigo-300 animate-pulse hover:animate-none transition scale-100 hover:scale-110"
                    >
                      <Volume2 size={14} /> {hs.word}
                    </button>
                  ))}
                </div>

                {/* Frame Narration Bar (Parsed with System Global HoverWord Parser) */}
                <div className="p-6 bg-white/95 backdrop-blur-md border-t border-slate-200">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
                      <Sparkles size={18} className="text-amber-500" /> {currentFrame.title_en}
                    </h3>
                    <span className="text-xs font-bold text-slate-400 font-mono">
                      Frame {activeFrameIndex + 1} of {storyScenes.length}
                    </span>
                  </div>

                  {/* Grammar X-Ray Toggle Bar */}
                  <div className="flex items-center gap-1.5 mb-3 bg-slate-100 rounded-xl p-1">
                    <button
                      onClick={() => setHighlightMode('clean')}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-black transition-all ${
                        highlightMode === 'clean'
                          ? 'bg-white text-slate-800 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      📖 Clean
                    </button>
                    <button
                      onClick={() => setHighlightMode('vocab')}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-black transition-all ${
                        highlightMode === 'vocab'
                          ? 'bg-indigo-500 text-white shadow-sm'
                          : 'text-slate-500 hover:text-indigo-600'
                      }`}
                    >
                      🔤 Vocab
                    </button>
                    <button
                      onClick={() => setHighlightMode('grammar')}
                      className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-black transition-all ${
                        highlightMode === 'grammar'
                          ? 'bg-amber-500 text-white shadow-sm'
                          : 'text-slate-500 hover:text-amber-600'
                      }`}
                    >
                      🔬 Grammar
                    </button>
                  </div>

                  <div className="text-sm font-bold text-slate-700 leading-relaxed">
                    {handleRenderParsedText(currentFrame.description_en, 'indigo')}
                  </div>
                </div>
              </div>

              {/* Webtoon Frame Selector Carousel */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2">
                <div className="text-xs font-black text-slate-500 uppercase tracking-wider">
                  Story Scenes (Click to Jump Frame):
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                  {storyScenes.map((frame, idx) => (
                    <button
                      key={frame.scene_id || idx}
                      onClick={() => setActiveFrameIndex(idx)}
                      className={`p-2 rounded-xl border-2 text-left transition-all ${
                        activeFrameIndex === idx
                          ? 'border-indigo-600 bg-white shadow-md ring-2 ring-indigo-200'
                          : 'border-slate-200 bg-white hover:border-indigo-300'
                      }`}
                    >
                      <div className="w-full h-16 rounded-xl overflow-hidden bg-slate-200 mb-2 relative">
                        <img src={frame.image_url} alt={frame.title_en} className="w-full h-full object-cover" />
                      </div>
                      <div className="text-[11px] font-black text-slate-900 truncate">{frame.title_en}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {learnSubTab === 'rw_part1' && (
            <WordBankMatchingGrid
              customData={data?.rw_part1}
              onComplete={async (score) => {
                await learnerProgressService.logAttempt({
                  learnerId,
                  contentId: `w${weekNumber}_rw_part1`,
                  mode: 'learn',
                  result: score >= 80 ? 'correct' : 'incorrect',
                  score,
                  timeSpentSeconds: 60
                });
              }}
            />
          )}

          {learnSubTab === 'rw_part2' && (
            <DialogueAHCompleter
              customData={data?.rw_part2}
              onComplete={async (score) => {
                await learnerProgressService.logAttempt({
                  learnerId,
                  contentId: `w${weekNumber}_rw_part2`,
                  mode: 'learn',
                  result: score >= 80 ? 'correct' : 'incorrect',
                  score,
                  timeSpentSeconds: 60
                });
              }}
            />
          )}

          {learnSubTab === 'rw_part4' && (
            <InlineTextClozeDropdown
              customData={data?.rw_part4}
              onComplete={async (score) => {
                await learnerProgressService.logAttempt({
                  learnerId,
                  contentId: `w${weekNumber}_rw_part4`,
                  mode: 'learn',
                  result: score >= 80 ? 'correct' : 'incorrect',
                  score,
                  timeSpentSeconds: 60
                });
              }}
            />
          )}

          {learnSubTab === 'rw_part5' && (
            <TextExtractionCompleter
              customData={data?.rw_part5}
              onComplete={async (score) => {
                await learnerProgressService.logAttempt({
                  learnerId,
                  contentId: `w${weekNumber}_rw_part5`,
                  mode: 'learn',
                  result: score >= 80 ? 'correct' : 'incorrect',
                  score,
                  timeSpentSeconds: 60
                });
              }}
            />
          )}
        </div>
      ) : (
        /* CHECK MODE: LIGHTWEIGHT WEEKLY CAMBRIDGE READING MINI-CHECK (OPEN CLOZE 5 GAPS) */
        <div className="space-y-4 animate-in fade-in">
          <div className="p-4 bg-amber-50 border border-amber-300 rounded-2xl flex items-center justify-between text-amber-900 shadow-sm">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-600 shrink-0" />
              <span className="text-xs font-black">🎯 WEEKLY CHECK MODE (MINI READING TEST): Fill 5 missing words from memory without hints! (5 Gaps • Cambridge Part 6 Standard)</span>
            </div>
            <span className="text-[10px] font-black uppercase px-2.5 py-1 bg-amber-200 text-amber-900 rounded-lg shrink-0">5-Min Check</span>
          </div>

          <OpenClozeCompleter customData={data?.rw_part_6_check_mode || data?.rw_part_6} />
        </div>
      )}
    </div>
  );
}
